<template>
  <b-modal
    id="modal-approve-leave"
    ref="inner-modal"
    title="Approve Leave Request"
    ok-title="Approve"
    cancel-title="Cancel"
    ok-variant="primary"
    cancel-variant="outline-secondary"
    size="lg"
    modal-class="leave-action-modal"
    body-class="leave-action-modal__body"
    @ok.prevent="handleOk"
    @hidden="handleHidden"
  >
    <b-form v-if="leave" class="leave-action-form">
      <b-row class="leave-action-columns">
        <!-- Request Details -->
        <b-col cols="12" :md="balance ? 6 : 12" class="mb-1 mb-md-0">
          <b-card no-body class="border leave-action-panel">
            <b-card-body class="p-1">
              <div class="d-flex align-items-center flex-wrap mb-1 leave-action-panel__header">
                <h6 class="mb-0 leave-action-panel__title mr-2">
                  <feather-icon icon="CalendarIcon" size="14" class="mr-50 text-primary" />
                  {{ formatLeaveTypeLabel(leave.leave_type) }}
                </h6>
                <b-badge :variant="statusVariant" class="mb-0">{{ statusLabel }}</b-badge>
              </div>

              <div class="leave-detail-grid">
                <div class="leave-detail-item">
                  <div class="leave-detail-label">Employee</div>
                  <div class="leave-detail-value">{{ employeeName || '-' }}</div>
                </div>
                <div class="leave-detail-item">
                  <div class="leave-detail-label">Duration</div>
                  <div class="leave-detail-value">{{ durationLabel }}</div>
                </div>
                <div class="leave-detail-item">
                  <div class="leave-detail-label">From</div>
                  <div class="leave-detail-value">{{ formatDate(leave.from_date) || '-' }}</div>
                </div>
                <div class="leave-detail-item">
                  <div class="leave-detail-label">To</div>
                  <div class="leave-detail-value">{{ formatDate(leave.to_date) || '-' }}</div>
                </div>
              </div>

              <div class="mt-50">
                <div class="leave-detail-label">Comments</div>
                <p class="mb-0 leave-detail-note">{{ leave.comment || 'No comments' }}</p>
              </div>
              <div class="mt-50">
                <div class="leave-detail-label">Remarks by Manager</div>
                <p class="mb-0 leave-detail-note">{{ leave.manager_remarks || leave.manager_comment || 'No remarks from manager' }}</p>
              </div>
            </b-card-body>
          </b-card>
        </b-col>

        <!-- Leave Summary (balance + applied) -->
        <b-col v-if="balance" cols="12" md="6" class="mb-0">
          <b-card no-body class="border leave-action-panel">
            <b-card-body class="p-1">
              <div class="d-flex justify-content-between align-items-center flex-wrap mb-1 leave-action-panel__header">
                <h6 class="mb-0 leave-action-panel__title text-primary">Leave Summary</h6>
                <div class="d-flex align-items-center flex-wrap">
                  <b-badge variant="light-primary" class="px-2 leave-action-badge mr-50">
                    Avail: {{ roundToHalf(balance.leaveBalance) }}
                  </b-badge>
                  <b-badge variant="light-info" class="px-2 leave-action-badge">
                    Used: {{ roundToHalf(balance.leavesApplied) }}
                  </b-badge>
                </div>
              </div>
              <b-table
                v-if="balance.summaryRows && balance.summaryRows.length > 0"
                :items="balance.summaryRows"
                :fields="summaryFields"
                small
                bordered
                responsive
                thead-class="leave-action-table__thead"
                class="mb-0 leave-action-table"
              >
                <template #cell(available)="row">
                  <span :class="{ 'text-muted': !(row.item.available > 0) }">
                    {{ row.item.available > 0 ? roundToHalf(row.item.available) : '—' }}
                  </span>
                </template>
                <template #cell(used)="row">
                  {{ row.item.used > 0 ? roundToHalf(row.item.used) : 0 }}
                </template>
              </b-table>
              <p v-else class="text-muted small mb-0">No leave data available</p>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <b-form-group
        label="Approval Comment"
        label-for="approval-comment"
        class="mb-0 mt-1 leave-action-comment"
        label-class="small font-weight-bolder mb-50"
      >
        <b-form-textarea
          id="approval-comment"
          v-model="localComment"
          rows="2"
          max-rows="3"
          placeholder="Add a short approval comment"
          :state="commentState"
          class="leave-action-textarea"
          @blur="commentTouched = true"
          @input="onCommentInput"
        />
        <b-form-invalid-feedback :state="commentState">
          Comment is mandatory for approval
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
        v-if="showPaidOption"
        label="Do you want to mark this as Paid Holiday?"
        class="mb-0"
        label-class="small font-weight-bolder mb-50"
      >
        <b-form-radio-group
          v-model="localIsPaid"
          :options="[{ value: true, text: 'Yes' }, { value: false, text: 'No' }]"
        />
      </b-form-group>
    </b-form>
  </b-modal>
</template>

<script>
import moment from 'moment'
import {
  BModal, BForm, BFormGroup, BFormTextarea, BFormInvalidFeedback, BBadge, BCard, BCardBody, BTable, BFormRadioGroup, BRow, BCol,
} from 'bootstrap-vue'

export default {
  name: 'ApproveLeaveModal',
  components: {
    BModal, BForm, BFormGroup, BFormTextarea, BFormInvalidFeedback, BBadge, BCard, BCardBody, BTable, BFormRadioGroup, BRow, BCol,
  },
  props: {
    leave: { type: Object, default: null },
    balance: { type: Object, default: null },
    employeeName: { type: String, default: '' },
    showPaidOption: { type: Boolean, default: false },
    formatLeaveType: { type: Function, default: null },
    formatApprovalStatus: { type: Function, default: null },
    getApprovalStatusVariant: { type: Function, default: null },
  },
  data() {
    return {
      localComment: '',
      localIsPaid: true,
      commentTouched: false,
      commentSubmitAttempted: false,
      summaryFields: [
        { key: 'label', label: 'Leave Type', thClass: 'py-1', tdClass: 'py-1' },
        { key: 'available', label: 'Available (Days)', thClass: 'py-1 text-right', tdClass: 'py-1 text-right font-weight-bolder' },
        { key: 'used', label: 'Used (Days)', thClass: 'py-1 text-right', tdClass: 'py-1 text-right font-weight-bolder' },
      ],
    }
  },
  computed: {
    hasComment() {
      return !!(this.localComment && this.localComment.trim())
    },
    commentState() {
      if (!(this.commentTouched || this.commentSubmitAttempted)) return null
      return this.hasComment
    },
    durationLabel() {
      if (!this.leave || this.leave.total_days == null) return '-'
      const days = parseFloat(this.leave.total_days)
      if (Number.isNaN(days)) return '-'
      return days === 1 ? '1 day' : `${days} days`
    },
    statusLabel() {
      const status = this.leave && this.leave.status
      if (typeof this.formatApprovalStatus === 'function') {
        return this.formatApprovalStatus(status) || status || 'Pending'
      }
      return status || 'Pending'
    },
    statusVariant() {
      const status = this.leave && this.leave.status
      if (typeof this.getApprovalStatusVariant === 'function') {
        return this.getApprovalStatusVariant(status) || 'secondary'
      }
      return 'secondary'
    },
  },
  methods: {
    formatLeaveTypeLabel(type) {
      if (typeof this.formatLeaveType === 'function') return this.formatLeaveType(type) || type || '-'
      return type || '-'
    },
    formatDate(date) {
      if (!date) return ''
      return moment(date).format('DD MMM YYYY')
    },
    roundToHalf(value) {
      if (value === null || value === undefined) return 0
      return Math.round(value * 2) / 2
    },
    onCommentInput() {
      if (this.hasComment) {
        this.commentSubmitAttempted = false
      }
    },
    handleOk() {
      this.commentSubmitAttempted = true
      if (!this.hasComment) return
      this.$emit('confirm', { comment: this.localComment.trim(), isPaid: this.localIsPaid })
    },
    handleHidden() {
      this.localComment = ''
      this.localIsPaid = true
      this.commentTouched = false
      this.commentSubmitAttempted = false
      this.$emit('hidden')
    },
    show() {
      this.$refs['inner-modal'].show()
    },
    hide() {
      this.$refs['inner-modal'].hide()
    },
  },
}
</script>

<style lang="scss">
.leave-action-modal {
  .modal-dialog {
    max-width: 860px;
  }

  .modal-header {
    padding: 0.5rem 0.75rem;
  }

  .modal-footer {
    padding: 0.5rem 0.75rem;
  }

  .leave-action-modal__body {
    padding: 0.5rem 0.75rem;
  }

  .leave-action-columns {
    align-items: flex-start;
    margin-bottom: 0;
  }

  .leave-action-columns > [class*='col'] {
    margin-bottom: 0.5rem;
  }

  .leave-action-panel {
    background: #f8f9fb;
    border-color: #e5e7eb !important;
    box-shadow: none;
    height: auto;

    .card-body {
      padding: 0.45rem 0.55rem !important;
    }
  }

  .leave-action-panel__header {
    min-height: auto;
    margin-bottom: 0.35rem !important;
  }

  .leave-action-panel__title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.25;
  }

  .leave-action-badge {
    font-size: 0.7rem;
    font-weight: 700;
    min-width: 1.75rem;
    text-align: center;
  }

  .leave-detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.25rem 0.5rem;
  }

  .leave-detail-label {
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 0.05rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
  }

  .leave-detail-value {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.3;
    color: #111827;
  }

  .leave-detail-note {
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.35;
    color: #4b5563;
    max-height: 2.7em;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .leave-action-table {
    font-size: 0.72rem;
    background: #fff;
    margin-bottom: 0 !important;

    th,
    td {
      padding: 0.2rem 0.35rem !important;
      vertical-align: middle;
    }

    thead th,
    .leave-action-table__thead th {
      background: #e6f4f1;
      border-bottom-width: 1px;
      font-weight: 700;
      font-size: 0.68rem;
      color: #0f766e;
      white-space: nowrap;
    }

    tbody td {
      color: #1f2937;
      font-weight: 500;
    }
  }

  .leave-action-comment {
    margin-bottom: 0 !important;
    margin-top: 0.35rem !important;

    .col-form-label {
      color: #1f2937;
      margin-bottom: 0.25rem !important;
    }
  }

  .leave-action-textarea {
    font-size: 0.8125rem;
    min-height: 44px;
  }

  .modal-footer .btn-primary {
    background-color: #0d9488;
    border-color: #0d9488;
    font-weight: 600;
  }

  .modal-footer .btn-primary:hover:not(:disabled),
  .modal-footer .btn-primary:focus:not(:disabled) {
    background-color: #0f766e;
    border-color: #0f766e;
  }

  .modal-footer .btn-primary:disabled {
    opacity: 0.65;
  }
}

@media (max-width: 767px) {
  .leave-action-modal {
    .modal-dialog {
      margin: 0.4rem auto;
      max-width: calc(100% - 0.8rem);
      width: calc(100% - 0.8rem);
    }

    .leave-action-modal__body {
      padding: 0.4rem 0.55rem;
    }

    .leave-action-columns > [class*='col'] {
      margin-bottom: 0.35rem;
    }

    .leave-action-panel .card-body {
      padding: 0.4rem 0.5rem !important;
    }

    .leave-detail-grid {
      grid-template-columns: 1fr;
      gap: 0.2rem;
    }

    .leave-action-table {
      display: block;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      max-width: 100%;
    }

    .modal-footer .btn {
      min-height: 36px;
    }
  }
}

@media (max-width: 575px) {
  .leave-action-modal .modal-dialog {
    margin: 0.25rem auto;
    max-width: calc(100% - 0.5rem);
    width: calc(100% - 0.5rem);
  }
}
</style>
